import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const Footer = () => {
  const { t } = useTranslation()
  
  const navItems = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.features'), href: '/features' },
    // { name: t('navigation.pricing'), href: '/pricing' }, // Hidden for now
    { name: t('navigation.build'), href: '/build' },
    { name: t('navigation.industries'), href: '/industries' },
    { name: t('navigation.contact'), href: '/contact' }
  ]

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white py-12 sm:py-16">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 left-0 w-48 h-48 sm:w-96 sm:h-96 bg-purple-500/10 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                KonvoAI
              </h3>
              <p className="text-gray-300 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base max-w-md">
                {t('footer.description')}
              </p>

              {/* Newsletter Signup */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 rtl:flex-row-reverse">
                <input
                  type="email"
                  placeholder={t('footer.emailPlaceholder')}
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 text-sm sm:text-base whitespace-nowrap"
                >
                  {t('footer.subscribe')}
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Navigation Links */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">{t('footer.navigation')}</h4>
              <ul className="space-y-2 sm:space-y-3">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      to={item.href}
                      className="text-gray-300 hover:text-white transition-colors duration-300 text-sm sm:text-base block hover:translate-x-1 rtl:hover:-translate-x-1 transform transition-transform"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">{t('footer.contact')}</h4>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-300 text-sm sm:text-base">{t('footer.email')}</p>
                {/* <p className="text-gray-300 text-sm sm:text-base">+91 9160741555</p> */}

                {/* Social Links */}
                {/* <div className="flex space-x-4 pt-2">
                  {['Twitter', 'LinkedIn', 'Facebook'].map((social, index) => (
                    <motion.a
                      key={index}
                      href="#"
                      whileHover={{ scale: 1.2, y: -2 }}
                      className="w-8 h-8 sm:w-10 sm:h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-purple-600 transition-all duration-300"
                    >
                      <span className="text-xs sm:text-sm font-bold">{social[0]}</span>
                    </motion.a>
                  ))}
                </div> */}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="border-t border-white/10 mt-8 sm:mt-12 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0"
        >
          <p className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
            {t('footer.copyright')}
          </p>
          <div className="flex space-x-6 rtl:space-x-reverse text-xs sm:text-sm">
            <Link
              to="/terms"
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              {t('footer.terms')}
            </Link>
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              {t('footer.privacy')}
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}

export default Footer